/* gp-nav.js — navigatie voor de GP header: desktop mega menu, mobiel drawer met uitklapbare
   niveaus, en instant search. Vanilla, geen dependencies, geen aanraking van theme.js.

   Twee dingen die niet mogen veranderen:
   1. Elk paneel is een absoluut gepositioneerd kind van <gp-header>. gp-pdp.js meet
      header.offsetHeight tegen header.parentElement.offsetHeight om te bepalen of de sticky
      productfoto ruimte moet reserveren; een paneel dat de sectie hoger maakt, verschuift die foto.
   2. De scroll-lock loopt via de class .gp-lock op <html>, niet via style.overflow. De cart drawer
      van het thema beheert zijn eigen lock; twee schrijvers op dezelfde property wissen elkaar. */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 1000px)';
  var FINE_POINTER = '(pointer: fine)';
  var HOVER_CLOSE_DELAY = 220;   // grace-tijd zodat je diagonaal naar een link kunt bewegen
  var SEARCH_DEBOUNCE = 280;     // zelfde waarde als de predictive search van het basisthema
  var CACHE_MAX = 30;

  function matches(q) { return window.matchMedia(q).matches; }

  function focusable(root) {
    var sel = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
    return Array.prototype.filter.call(root.querySelectorAll(sel), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  function init(root) {
    if (!root || root.dataset.gpNavBound) return;
    root.dataset.gpNavBound = '1';

    var ac = new AbortController();
    var opt = { signal: ac.signal };
    root._gpNavAbort = ac;

    var on = function (el, type, fn, extra) {
      if (!el) return;
      var o = { signal: ac.signal };
      if (extra) for (var k in extra) o[k] = extra[k];
      el.addEventListener(type, fn, o);
    };

    /* ============================ mega menu (desktop) ============================ */

    var navItems = Array.prototype.slice.call(root.querySelectorAll('.gp-header__nav-item.has-children'));
    var openItem = null;
    var closeTimer = null;

    function panelOf(li) { return li.querySelector('.gp-mega'); }
    function caretOf(li) { return li.querySelector('.gp-header__nav-caret'); }

    function closeMega(item) {
      var li = item || openItem;
      if (!li) return;
      var p = panelOf(li), c = caretOf(li);
      if (p) p.hidden = true;
      if (c) c.setAttribute('aria-expanded', 'false');
      li.classList.remove('is-open');
      if (openItem === li) openItem = null;
    }

    function openMega(li) {
      if (!li || li === openItem) return;
      if (openItem) closeMega(openItem);
      var p = panelOf(li), c = caretOf(li);
      if (!p) return;
      p.hidden = false;
      if (c) c.setAttribute('aria-expanded', 'true');
      li.classList.add('is-open');
      openItem = li;
    }

    navItems.forEach(function (li) {
      var caret = caretOf(li);

      // Hover alleen bij een echte muis. Op touch is de caret-knop het enige pad, zodat een tik
      // op het woord altijd navigeert in plaats van "eerste tik opent, tweede tik gaat".
      on(li, 'mouseenter', function () {
        if (!matches(DESKTOP) || !matches(FINE_POINTER)) return;
        clearTimeout(closeTimer);
        openMega(li);
      });

      on(caret, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (openItem === li) { closeMega(li); caret.focus(); }
        else {
          openMega(li);
          var first = panelOf(li).querySelector('a[href]');
          if (first && e.detail === 0) first.focus();   // toetsenbord (Enter/Space), niet bij muisklik
        }
      });

      // Tab uit het item weg sluit het paneel; binnen het item (kolommen) blijft het open.
      on(li, 'focusout', function (e) {
        if (!e.relatedTarget || !li.contains(e.relatedTarget)) closeMega(li);
      });
    });

    var nav = root.querySelector('.gp-header__nav');
    on(nav, 'mouseleave', function () {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () { closeMega(); }, HOVER_CLOSE_DELAY);
    });
    on(nav, 'mouseenter', function () { clearTimeout(closeTimer); });

    /* ============================ mobiel drawer ============================ */

    var drawer = root.querySelector('.gp-header__drawer');
    var scrim = root.querySelector('.gp-header__scrim');
    var burger = root.querySelector('[data-gp-menu-open]');

    function drawerOpen() { return drawer && !drawer.hidden; }

    function openDrawer() {
      if (!drawer) return;
      drawer.hidden = false;
      if (scrim) scrim.hidden = false;
      document.documentElement.classList.add('gp-lock');
      if (burger) burger.setAttribute('aria-expanded', 'true');
      var close = drawer.querySelector('[data-gp-menu-close]');
      if (close) close.focus();
    }

    function closeDrawer(returnFocus) {
      if (!drawer || drawer.hidden) return;
      drawer.hidden = true;
      if (scrim) scrim.hidden = true;
      document.documentElement.classList.remove('gp-lock');
      if (burger) {
        burger.setAttribute('aria-expanded', 'false');
        if (returnFocus !== false) burger.focus();
      }
    }

    root.querySelectorAll('[data-gp-menu-open]').forEach(function (b) { on(b, 'click', openDrawer); });
    root.querySelectorAll('[data-gp-menu-close]').forEach(function (b) { on(b, 'click', function () { closeDrawer(); }); });

    // Focus vasthouden zolang het menu open is.
    on(drawer, 'keydown', function (e) {
      if (e.key !== 'Tab' || drawer.hidden) return;
      var items = focusable(drawer);
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    // Uitklappen in het drawer (niveau 1 en 2).
    root.querySelectorAll('.gp-drawer-toggle').forEach(function (btn) {
      on(btn, 'click', function () {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
      });
    });

    /* ============================ zoeken ============================ */

    var search = root.querySelector('.gp-search');
    var triggers = Array.prototype.slice.call(root.querySelectorAll('[data-gp-search-open]'));
    var input = search && search.querySelector('input[name="q"]');
    var results = search && search.querySelector('[data-gp-results]');
    var idle = search && search.querySelector('[data-gp-idle]');
    var live = search && search.querySelector('[data-gp-live]');
    var cache = new Map();
    var timer = null;
    var inflight = null;

    function searchOpen() { return search && !search.hidden; }

    function openSearch() {
      if (!search) return;
      closeMega();
      closeDrawer(false);
      search.hidden = false;
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
      if (input) input.focus();   // synchroon, anders komt het toetsenbord op iOS niet op
    }

    function closeSearch(returnFocus) {
      if (!search || search.hidden) return;
      search.hidden = true;
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      if (returnFocus !== false && triggers.length) {
        var visible = triggers.filter(function (t) { return t.offsetWidth > 0 || t.offsetHeight > 0; });
        (visible[0] || triggers[0]).focus();
      }
    }

    triggers.forEach(function (t) {
      on(t, 'click', function (e) {
        e.preventDefault();
        if (searchOpen()) closeSearch(); else openSearch();
      });
    });
    root.querySelectorAll('[data-gp-search-close]').forEach(function (b) {
      on(b, 'click', function () { closeSearch(); });
    });

    function showIdle() {
      if (results) results.innerHTML = '';
      if (idle) idle.hidden = false;
      if (live) live.textContent = '';
    }

    function fetchResults(q) {
      var base = search.getAttribute('data-search-url');
      var url = base + '/suggest?q=' + encodeURIComponent(q) +
        '&section_id=' + search.getAttribute('data-search-section') +
        '&resources[type]=product,collection,query' +
        '&resources[limit]=' + (search.getAttribute('data-search-limit') || '6') +
        '&resources[limit_scope]=each' +
        '&resources[options][fields]=title,product_type,variants.title,tag' +
        '&resources[options][unavailable_products]=last';

      var cached = cache.get(q);
      if (cached) { paint(cached, q); return; }

      if (inflight) inflight.abort();
      inflight = new AbortController();
      fetch(url, { signal: inflight.signal })
        .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var frag = doc.querySelector('.gp-ps');
          if (!frag) return;                       // onverwacht antwoord: laat staan wat er stond
          if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
          cache.set(q, frag.innerHTML);
          paint(frag.innerHTML, q);
        })
        .catch(function (err) { if (err !== 'AbortError' && err && err.name !== 'AbortError') { /* stil: oude resultaten blijven staan */ } });
    }

    function paint(html, q) {
      if (!input || input.value.trim() !== q) return;   // ondertussen doorgetypt
      if (idle) idle.hidden = true;
      results.innerHTML = html;
      if (live) {
        var n = results.querySelectorAll('.gp-ps__row').length;
        live.textContent = n ? n + ' results for ' + q : 'No results for ' + q;
      }
    }

    if (input && search.getAttribute('data-search-instant') === 'true') {
      on(input, 'input', function () {
        var q = input.value.trim();
        clearTimeout(timer);
        if (inflight) { inflight.abort(); inflight = null; }
        if (!q) { showIdle(); return; }
        timer = setTimeout(function () { fetchResults(q); }, SEARCH_DEBOUNCE);
      });
    }

    /* ============================ globaal ============================ */

    on(document, 'keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (searchOpen()) { e.stopPropagation(); closeSearch(); return; }
      if (drawerOpen()) { e.stopPropagation(); closeDrawer(); return; }
      if (openItem) { e.stopPropagation(); var li = openItem; closeMega(li); var c = caretOf(li); if (c) c.focus(); }
    });

    on(document, 'click', function (e) {
      if (root.contains(e.target)) return;
      closeMega();
      if (searchOpen()) closeSearch(false);
    });

    // In de theme-editor het juiste paneel tonen als de merchant een mega-menu-block selecteert.
    on(document, 'shopify:block:select', function (e) {
      var item = navItems.filter(function (li) { return li.getAttribute('data-block-id') === e.detail.blockId; })[0];
      if (item) openMega(item);
    });
  }

  function boot() {
    document.querySelectorAll('gp-header').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', function (e) {
    var h = e.target.querySelector && e.target.querySelector('gp-header');
    if (h) init(h);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var h = e.target.querySelector && e.target.querySelector('gp-header');
    if (h && h._gpNavAbort) h._gpNavAbort.abort();
  });
})();
